import React from "react";
import {
    UserOutlined,
    MailOutlined,
    PhoneOutlined,
    HomeOutlined,
    CalendarOutlined,
    InfoCircleOutlined,
    TeamOutlined,
    BankOutlined,
    ClockCircleOutlined
} from "@ant-design/icons";
import { Col, Row, Tag } from "antd";
import CommonCard from "../../../global/card";
import { cn } from "../../../../utils/cn";
import { formatDateTime, formatLeadSource } from "../../../../utils/functions";

const EnhancedLeadImportedUserDetails = ({
    leadDetail,
    setShowDetail,
    onToggleHotLead
}) => {
    if (!leadDetail) return null;

    const SectionHeader = ({ icon, title }) => (
        <div className="flex items-center gap-2 mb-3 sm:mb-4">
            {React.cloneElement(icon, { style: { color: "var(--primary-color, #1890ff)", fontSize: "clamp(14px, 4vw, 16px)" } })}
            <span style={{ fontSize: "clamp(13px, 4vw, 15px)", fontWeight: 600 }}>{title}</span>
        </div>
    );

    const getIconForField = (label) => {
        const iconMap = {
            Name: <UserOutlined />,
            Email: <MailOutlined />,
            Phone: <PhoneOutlined />,
            Address: <HomeOutlined />,
            "Event Name": <InfoCircleOutlined />,
            "Event Type": <InfoCircleOutlined />,
            "Event Date": <CalendarOutlined />,
            "Event Time": <ClockCircleOutlined />,
            "Guest Count": <TeamOutlined />,
            Company: <BankOutlined />,
            "Imported At": <CalendarOutlined />,
            Source: <InfoCircleOutlined />,
        };
        return iconMap[label] || <InfoCircleOutlined />;
    };

    const InfoItem = ({ label, value, isTag, color, icon }) => (
        <Col xs={24} sm={12}>
            <div className="flex flex-col">
                <span className="text-xs text-gray-500 flex items-center gap-2 mb-1">
                    <span style={{ color: "var(--primary-color, #1890ff)" }}>
                        {getIconForField(label)}
                    </span>
                    {label}
                </span>
                {isTag ? (
                    <Tag color={color || "blue"} className="m-0 px-3 py-1 font-medium capitalize w-fit">
                        {value || "—"}
                    </Tag>
                ) : (
                    <span className="text-sm font-semibold text-gray-900">{value || "—"}</span>
                )}
            </div>
        </Col>
    );

    return (
        <div className="sm:!p-6 p-2 overflow-x-hidden" style={{ backgroundColor: "#f5f7fa" }}>
            <CommonCard
                cardHeadBodyClassName={{ head: "!px-6 !py-0", body: "!px-6 !py-0 !pb-4" }}
            >
                <Row gutter={[24, 0]}>
                    {/* Photo Section (if available) */}
                    {leadDetail.photo && (
                        <Col span={24} className="border-b border-gray-200 py-4 text-center">
                            <img
                                src={leadDetail.photo}
                                alt="Lead"
                                width={100}
                                style={{ borderRadius: "50%", margin: "0 auto" }}
                            />
                        </Col>
                    )}

                    {/* Contact Details Section */}
                    <Col span={24} className="border-b border-gray-200 py-4">
                        <SectionHeader icon={<UserOutlined />} title="Contact Information" />
                        <Row gutter={[{ xs: 16, sm: 24 }, { xs: 12, sm: 16 }]}>
                            <InfoItem
                                label="Name"
                                value={leadDetail.fullName ? leadDetail.fullName : `${leadDetail.firstName} ${leadDetail.lastName}`}
                            />
                            <InfoItem label="Email" value={leadDetail?.email} />
                            <InfoItem label="Phone" value={leadDetail?.phone} />
                            <InfoItem label="Address" value={leadDetail?.address} />
                            <InfoItem label="Company" value={leadDetail?.company} />
                        </Row>
                    </Col>

                    {/* Event Details Section */}
                    <Col span={24} className={cn("py-4", !leadDetail?.inquiry && "border-b-0")}>
                        <SectionHeader icon={<CalendarOutlined />} title="Event Information" />
                        <Row gutter={[{ xs: 16, sm: 24 }, { xs: 12, sm: 16 }]}>
                            <InfoItem label="Event Name" value={leadDetail?.eventName} />
                            <InfoItem label="Event Type" value={leadDetail?.eventType} />
                            <InfoItem label="Event Date" value={leadDetail?.eventDate} />
                            <InfoItem label="Event Time" value={leadDetail?.eventTime} />
                            <InfoItem label="Guest Count" value={leadDetail?.guestCount} />
                            <InfoItem label="Imported At" value={formatDateTime(leadDetail?.createdAt)} />
                            <InfoItem
                                label="Source"
                                value={formatLeadSource(leadDetail?.leadSource)}
                                isTag
                            />
                        </Row>
                    </Col>

                    {/* Notes/Inquiry Section */}
                    {leadDetail?.inquiry && (
                        <Col span={24} className="border-t border-gray-200 py-4">
                            <SectionHeader icon={<InfoCircleOutlined />} title="Notes/Inquiry" />
                            <div className="bg-white p-3 rounded-lg border border-gray-100 italic shadow-sm">
                                <p className="text-sm text-gray-700 whitespace-pre-wrap m-0">{leadDetail?.inquiry}</p>
                            </div>
                        </Col>
                    )}
                </Row>
            </CommonCard>
        </div>
    );
};

export default EnhancedLeadImportedUserDetails;
